// Editor-usage tab: turn list from GET /sites/{id}/editor-usage. Errored turns
// are surfaced first-class (SPEC_SITE_MANAGER_LOGS Capability 2).
import { useState } from 'react';
import type { FormEvent } from 'react';
import {
  useSiteEditorUsage,
  useSiteEditorKey,
  useSetEditorKey,
  useRemoveEditorKey,
} from '../hooks';
import { Conversations } from './Conversations';
import { QueryState } from '../../../components/QueryState';
import type { EditorKeyStatus, EditorUsageResponse, TurnStatus } from '../../../lib/types';

const ID = 'em-mgr--site--editor';

const STATUS_STYLE: Record<TurnStatus, string> = {
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  llm_error: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  auth_error: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  config_error: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
};

export interface EditorTabProps {
  siteId: string;
  /** Expanded conversation from the URL (?session=). */
  session?: string | null;
  onSessionChange?: (session: string | null) => void;
}

export function EditorTab({ siteId, session, onSessionChange }: EditorTabProps) {
  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-2 text-sm font-semibold">Site Assistant API key</h3>
        <EditorKeyPanel siteId={siteId} />
      </section>
      <section>
        <h3 className="mb-2 text-sm font-semibold">Conversations</h3>
        <Conversations
          siteId={siteId}
          session={session}
          onSessionChange={onSessionChange}
        />
      </section>
      <section>
        <h3 className="mb-2 text-sm font-semibold">Turn metadata</h3>
        <EditorTurnTable siteId={siteId} />
      </section>
    </div>
  );
}

function EditorKeyPanel({ siteId }: { siteId: string }) {
  const status = useSiteEditorKey(siteId);
  const setKey = useSetEditorKey(siteId);
  const removeKey = useRemoveEditorKey(siteId);
  const [value, setValue] = useState('');
  const busy = setKey.isPending || removeKey.isPending;

  function submit(e: FormEvent) {
    e.preventDefault();
    const key = value.trim();
    if (!key) return;
    setKey.mutate(key, { onSuccess: () => setValue('') });
  }

  const mutationError = setKey.error ?? removeKey.error;

  return (
    <QueryState<EditorKeyStatus>
      idBase={`${ID}--key`}
      isPending={status.isPending}
      isError={status.isError}
      error={status.error}
      data={status.data}
    >
      {(s) => (
        <div
          data-id={`${ID}--key`}
          className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-800"
        >
          <div className="flex items-center gap-2 text-sm">
            {s.keyConfigured ? (
              <>
                <span
                  data-id={`${ID}--key--set`}
                  className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300"
                >
                  Key set
                </span>
                <span className="font-mono text-slate-500">••••{s.keyHint}</span>
              </>
            ) : (
              <span
                data-id={`${ID}--key--unset`}
                className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
              >
                No key — falls back to the fleet-wide key
              </span>
            )}
          </div>

          <form onSubmit={submit} className="space-y-2">
            <label className="block text-sm">
              <span className="text-slate-500">
                {s.keyConfigured ? 'Replace key' : 'Set key'}
              </span>
              <input
                data-id={`${ID}--key--input`}
                type="password"
                autoComplete="off"
                className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 font-mono text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="sk-ant-…"
              />
            </label>
            <div className="flex gap-2">
              <button
                type="submit"
                data-id={`${ID}--key--save`}
                disabled={busy || !value.trim()}
                className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300"
              >
                {setKey.isPending ? 'Saving…' : s.keyConfigured ? 'Replace' : 'Save'}
              </button>
              {s.keyConfigured && (
                <button
                  type="button"
                  data-id={`${ID}--key--remove`}
                  disabled={busy}
                  onClick={() => removeKey.mutate()}
                  className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-slate-700 dark:text-red-400 dark:hover:bg-red-950/40"
                >
                  {removeKey.isPending ? 'Removing…' : 'Remove'}
                </button>
              )}
            </div>
            {mutationError && (
              <p data-id={`${ID}--key--error`} className="text-sm text-red-600 dark:text-red-400">
                {mutationError instanceof Error ? mutationError.message : 'Request failed'}
              </p>
            )}
          </form>
        </div>
      )}
    </QueryState>
  );
}

function EditorTurnTable({ siteId }: { siteId: string }) {
  const { data, isPending, isError, error } = useSiteEditorUsage(siteId);
  return (
    <QueryState<EditorUsageResponse>
      idBase={ID}
      isPending={isPending}
      isError={isError}
      error={error}
      data={data}
    >
      {(d) =>
        d.turns.length === 0 ? (
          <div data-id={`${ID}--empty`} className="p-4 text-sm text-slate-500">
            No editor turns recorded.
          </div>
        ) : (
          <div data-id={ID} className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
                <tr>
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Origin</th>
                  <th className="px-3 py-2">Model</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Tools</th>
                  <th className="px-3 py-2">Latency</th>
                </tr>
              </thead>
              <tbody>
                {d.turns.map((t) => (
                  <tr key={t.turnId} data-id={`${ID}--turn`} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2 text-slate-500">{t.ts}</td>
                    <td className="px-3 py-2">{t.wpUser}</td>
                    <td className="px-3 py-2 text-slate-500">{t.origin}</td>
                    <td className="px-3 py-2">{t.model}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[t.status]}`}>
                        {t.status}
                      </span>
                      {t.errorClass && (
                        <span className="ml-1 text-xs text-red-600 dark:text-red-400">{t.errorClass}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{t.toolCallCount}</td>
                    <td className="px-3 py-2 text-slate-500">{t.latencyMs}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </QueryState>
  );
}
